import React, { useState, useMemo } from 'react';
import { Calendar, Download, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { crumbsFor } from '../../navigation/modules';
import { useQuery } from '@tanstack/react-query';
import api from '../../services/api';

// Team Attendance: one day's roster for everyone the viewer may see.
// ADMIN/HR see the whole organisation; anyone else sees their whole
// reporting tree (so the head of the org sees everyone). The server decides
// the scope; this page just renders what it's allowed to return.

const STATUS_STYLE = {
    PRESENT: { label: 'Present', bg: '#dcfce7', fg: '#166534' },
    HALF_DAY: { label: 'Half day', bg: '#fef9c3', fg: '#854d0e' },
    ON_LEAVE: { label: 'On leave', bg: '#dbeafe', fg: '#1e40af' },
    ABSENT: { label: 'Absent', bg: '#fee2e2', fg: '#991b1b' },
    HOLIDAY: { label: 'Holiday', bg: '#f3e8ff', fg: '#6b21a8' },
    WEEKEND: { label: 'Weekend', bg: '#f3f4f6', fg: '#4b5563' },
};

const localDateKey = (d = new Date()) => {
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const shiftDate = (key, days) => {
    const [y, m, d] = key.split('-').map(Number);
    return localDateKey(new Date(y, m - 1, d + days));
};

const formatTime = (t) => (t ? new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '–');

const AttendanceManagement = () => {
    // Which breadcrumb this page sits under: HR/Admin reach it as the organisation's Attendance overview
    // (under People); everyone else with reports reaches it as their own Team attendance.
    const role = (JSON.parse(localStorage.getItem('user') || '{}')).role;
    const isHrOrAdmin = role === 'ADMIN' || role === 'HR';
    const [date, setDate] = useState(localDateKey());
    const [searchTerm, setSearchTerm] = useState('');
    const [department, setDepartment] = useState('');
    const [status, setStatus] = useState('');

    const { data: roster, isLoading: loading, error: queryError } = useQuery({
        queryKey: ['team-attendance', date],
        queryFn: () => api.get(`/attendance/team?date=${date}`).then(r => r.data),
    });
    const error = queryError ? (queryError.response?.data?.error || 'Failed to load attendance') : '';

    const departments = useMemo(
        () => [...new Set((roster?.rows || []).map(r => r.department).filter(Boolean))].sort(),
        [roster],
    );
    const rows = roster?.rows || [];

    const filtered = rows.filter(r => {
        const q = searchTerm.toLowerCase();
        const matchesSearch = !q || r.name.toLowerCase().includes(q) || (r.employeeId || '').toLowerCase().includes(q);
        return matchesSearch && (!department || r.department === department) && (!status || r.status === status);
    });

    const exportCsv = () => {
        const q = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
        const header = ['Date', 'Employee', 'Employee ID', 'Department', 'Manager', 'Status', 'Check In', 'Check Out', 'Hours', 'Leave'];
        const lines = filtered.map(r => [
            roster.date, r.name, r.employeeId, r.department, r.managerName,
            STATUS_STYLE[r.status]?.label || r.status, formatTime(r.checkIn), formatTime(r.checkOut), r.hours ?? '',
            r.leave ? `${r.leave.type}${r.leave.isHalfDay ? ' (half day)' : ''}` : '',
        ].map(q).join(','));
        const blob = new Blob([[header.join(','), ...lines].join('\n')], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `attendance-${roster.date}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const scopeLabel = roster?.scope?.all
        ? 'Whole organisation'
        : roster?.scope?.teamSize
            ? `You and your team (${roster.scope.teamSize} ${roster.scope.teamSize === 1 ? 'person' : 'people'})`
            : 'Only you — no one reports to you';

    return (
        <div className="container section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <PageHeader crumbs={crumbsFor(isHrOrAdmin ? 'orgAttendance' : 'teamAttendance')} />
                <button className="btn btn-secondary" onClick={exportCsv} disabled={!filtered.length} style={{ display: 'flex', alignItems: 'center' }}>
                    <Download size={18} style={{ marginRight: '0.5rem' }} /> Export CSV
                </button>
            </div>

            <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--text-main)', marginBottom: '0.25rem', display: 'flex', alignItems: 'center' }}>
                <Calendar style={{ marginRight: '0.75rem', color: '#2563eb' }} /> Team Attendance
            </h1>
            <p style={{ color: 'var(--text-muted)', marginBottom: '1.5rem', fontSize: '0.9rem' }}>{scopeLabel}</p>

            <div className="glass-card" style={{ padding: '1.25rem', marginBottom: '1rem' }}>
                <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                        <button className="btn btn-secondary" onClick={() => setDate(d => shiftDate(d, -1))} aria-label="Previous day" style={{ padding: '0.6rem' }}>
                            <ChevronLeft size={16} />
                        </button>
                        <input type="date" value={date} max={localDateKey()} onChange={e => e.target.value && setDate(e.target.value)}
                            style={{ padding: '0.6rem', borderRadius: '0.5rem', border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text-main)' }} />
                        <button className="btn btn-secondary" onClick={() => setDate(d => shiftDate(d, 1))} disabled={date >= localDateKey()} aria-label="Next day" style={{ padding: '0.6rem' }}>
                            <ChevronRight size={16} />
                        </button>
                    </div>
                    <div style={{ position: 'relative', flex: 1, minWidth: '200px' }}>
                        <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                        <input
                            type="text"
                            placeholder="Search by employee name or ID..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{ width: '100%', padding: '0.6rem 0.75rem 0.6rem 2.5rem', borderRadius: '0.5rem', border: '1px solid var(--border)' }}
                        />
                    </div>
                    <select value={department} onChange={e => setDepartment(e.target.value)}
                        style={{ padding: '0.6rem', borderRadius: '0.5rem', border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text-main)' }}>
                        <option value="">All departments</option>
                        {departments.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                </div>
            </div>

            {roster && (
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem', alignItems: 'center' }}>
                    {roster.dayType !== 'WORKING' && (
                        <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginRight: '0.5rem' }}>
                            {roster.dayType === 'HOLIDAY' ? `Holiday: ${roster.holidayName}` : 'Weekend'}
                        </span>
                    )}
                    {Object.entries(roster.summary).filter(([, n]) => n > 0).map(([s, n]) => (
                        <button key={s} onClick={() => setStatus(status === s ? '' : s)} style={{
                            padding: '0.3rem 0.75rem', borderRadius: '9999px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
                            border: status === s ? `2px solid ${STATUS_STYLE[s].fg}` : '2px solid transparent',
                            background: STATUS_STYLE[s].bg, color: STATUS_STYLE[s].fg,
                        }}>
                            {STATUS_STYLE[s].label}: {n}
                        </button>
                    ))}
                    {status && (
                        <button onClick={() => setStatus('')} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '0.8rem' }}>
                            Clear filter
                        </button>
                    )}
                </div>
            )}

            <div className="glass-card" style={{ padding: 0, overflow: 'hidden' }}>
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                        <thead>
                            <tr style={{ backgroundColor: 'var(--bg-secondary)', borderBottom: '1px solid var(--border)' }}>
                                {['Employee', 'Manager', 'Status', 'Check In', 'Check Out', 'Hours', 'Leave'].map(h => (
                                    <th key={h} style={{ padding: '1rem', fontWeight: '600', color: 'var(--text-secondary)' }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="7" style={{ padding: '2rem', textAlign: 'center' }}>Loading...</td></tr>
                            ) : error ? (
                                <tr><td colSpan="7" style={{ padding: '2rem', textAlign: 'center', color: '#991b1b' }}>{error}</td></tr>
                            ) : filtered.length === 0 ? (
                                <tr><td colSpan="7" style={{ padding: '2rem', textAlign: 'center' }}>No records</td></tr>
                            ) : (
                                filtered.map(r => {
                                    const s = STATUS_STYLE[r.status] || { label: r.status, bg: '#f3f4f6', fg: '#374151' };
                                    return (
                                        <tr key={r.userId} style={{ borderBottom: '1px solid var(--border)' }}>
                                            <td style={{ padding: '1rem' }}>
                                                <div style={{ fontWeight: '500' }}>{r.name}</div>
                                                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                                                    {[r.employeeId, r.department].filter(Boolean).join(' • ')}
                                                </div>
                                            </td>
                                            <td style={{ padding: '1rem', fontSize: '0.875rem', color: 'var(--text-muted)' }}>{r.managerName || '–'}</td>
                                            <td style={{ padding: '1rem' }}>
                                                <span style={{ padding: '0.25rem 0.6rem', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: '600', backgroundColor: s.bg, color: s.fg }}>
                                                    {s.label}
                                                </span>
                                            </td>
                                            <td style={{ padding: '1rem', color: '#059669', fontWeight: '500' }}>{formatTime(r.checkIn)}</td>
                                            <td style={{ padding: '1rem', color: '#dc2626', fontWeight: '500' }}>{formatTime(r.checkOut)}</td>
                                            <td style={{ padding: '1rem' }}>{r.hours != null ? `${r.hours.toFixed(2)} hrs` : '–'}</td>
                                            <td style={{ padding: '1rem', fontSize: '0.875rem' }}>
                                                {r.leave ? `${r.leave.type}${r.leave.isHalfDay ? ' (½ day)' : ''}` : '–'}
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default AttendanceManagement;
